/**
 * English — the **key master**. Every other locale is typed against
 * `keyof typeof en`, so a key added here and forgotten elsewhere is a compile
 * error rather than a blank label on a field device. That is the web stand-in
 * for Android's `MissingTranslation` lint check.
 *
 * Keys are the Android `strings.xml` names **verbatim**, and the values are
 * copied from `values/strings.xml` rather than rewritten, so the two apps say
 * the same sentence to the same operator. Android's positional format args are
 * converted on the way in: `%1$s` → `{0}`, `%2$s` → `{1}`, `%3$s` → `{2}`,
 * `%%` → `%`.
 *
 * Android keys deliberately **not** ported, because the thing they describe does
 * not exist inside Safari and a translated string for it would imply it does:
 * `notif_*` (no notifications without a native app), `update_*` (a WebClip
 * reloads whatever the host serves — there is no APK to install), `perm_*` and
 * `settings_open_app_settings` (no runtime permission model), `settings_battery_*`
 * and `settings_autostart_*` (unreachable from a web page — replaced by the
 * read-only checklist in `ios_setup_*`), `settings_interval_floor` (a WorkManager
 * limit, not ours), `device_os*` (SPEC §8 forbids scraping the OS version out of
 * the UA string), `home_browser_round` (no browser verification round, SPEC §14
 * #8), `home_verifying_network` (`navigator.onLine` answers instantly — there is
 * nothing to show a spinner for), `a11y_open_in_browser` (leaving the WebClip
 * suspends the page and kills the running cycle), and `result_http_code` — the
 * important one: `statusCode` is always null here (SPEC §6), so a string that
 * renders an HTTP code could only ever render a fabricated one.
 *
 * A second group was ported and then **removed**, because nothing on screen ever
 * rendered them and an unused string is one somebody later wires into the wrong
 * place: `common_close` and `common_retry` (no dialog here has a close button,
 * and nothing offers a retry — a parked cycle resumes on its own),
 * `err_offline_cannot_start` (Android's tap-Start-with-no-network snackbar; a
 * cycle here does not refuse, it parks in `waiting-for-network`, and the notice
 * row says so permanently rather than for four seconds — see `HomeScreen.tsx`),
 * `err_unknown` (a browser `fetch` rejects with "Load failed" for a DNS failure,
 * an unanswered CORS preflight, a timeout and an offline radio alike, so there
 * is no honest message to fall back to — see `KeyEntryScreen.tsx`), and
 * `ios_phase_idle` / `ios_phase_done` / `ios_phase_failed` (the progress bar,
 * the batch counts and the error module already say those three phases, with the
 * numbers attached — see `ProgressNotice`).
 */
export const en = {
  app_name: 'CheckLinks',

  // Bottom navigation
  nav_dashboard: 'Dashboard',
  nav_results: 'Results',
  nav_settings: 'Settings',

  // Common
  common_see_all: 'See all',
  common_cancel: 'Cancel',
  value_none: '—',
  value_unknown: 'Unknown',
  unit_ms: '{0} ms',

  // Network status. Only the two states this platform can honestly report:
  // Android's net_wifi / net_mobile / net_no_internet are omitted because
  // Safari on iOS has no Network Information API, and printing "Wi-Fi" or "4G"
  // there would be a guess dressed up as a measurement (SPEC §14 #5).
  net_offline: 'Offline',
  net_no_connection_body: 'No network connection — this device cannot check links.',

  // Dashboard
  home_progress_label: 'Progress',
  home_percent: '{0}%',
  home_checked_of_total: '{0} / {1}',
  home_waiting_network: 'No internet — waiting. The check resumes on its own.',
  home_round: 'Round {0}/{1} — {2} links not yet passing',
  home_next_round: 'Round {0} starts in {1}',
  home_recent_label: 'Recent',
  home_start: 'Start check',
  home_run_again: 'Run again',
  home_stop: 'Stop check',
  home_view_results: 'View results',
  stat_alive: 'Alive',
  stat_dead: 'Dead',
  stat_response: 'Response',

  // Errors
  err_generic_title: 'Something went wrong',
  err_try_again_later: 'Try again later.',
  err_key_invalid: 'Device key is invalid or has been revoked.',
  err_key_required: 'Enter a device key.',
  err_server_unreachable: 'Cannot reach the server. Check your connection.',

  // Accessibility
  a11y_change_language: 'Change language',
  a11y_copy: 'Copy',

  // Settings
  settings_language: 'Language',
  settings_section_language: 'Language',
  settings_measurement: 'Measurement',
  settings_measurement_value: '{0} rounds, {1}s apart — set by the server',
  settings_measurement_unknown: 'Not received from the server yet',
  settings_section_device: 'Device',
  device_model: 'Model',
  device_app_version: 'App version',
  device_network: 'Network',
  device_carrier: 'Carrier',
  device_region: 'Region',
  device_ip: 'IP address',
  // Android's version of this key is "Android %1$s · v%2$s" — hardcoding the OS
  // name, which it can do because it knows it. Here {0} is the device model from
  // `deviceInfo()`; the OS version is deliberately absent (SPEC §8).
  device_summary: '{0} · v{1}',

  // Results
  filter_all: 'All {0}',
  filter_alive: 'Alive {0}',
  filter_dead: 'Dead {0}',
  result_empty: 'No links',
  result_copied: 'Copied',

  // Key entry
  keyentry_subtitle: 'Enter the device key you were issued to begin',
  keyentry_field_label: 'Device key (dvk_…)',
  keyentry_submit: 'Verify and save',

  // Device key removal
  settings_section_device_key: 'Device key',
  device_key_remove: 'Remove the key on this device',
  device_key_remove_title: 'Remove this device key?',
  device_key_remove_body:
    'The key stored on this phone will be deleted. Scheduled checks stop and auto-run is switched off. You will need the key again to set the device up.',
  device_key_remove_action: 'Remove key',
  device_key_removing: 'Removing the key…',
  device_key_removing_body: 'Stopping the checks that are still running.',

  // ---------------------------------------------------------------------------
  // WebClip-only. Everything below has no Android counterpart, either because it
  // describes a limit this platform has and Android does not, or because it
  // names something Android solves with a mechanism Safari has no access to.
  // ---------------------------------------------------------------------------

  // Android reports Wi-Fi / mobile / generation. We can only tell connected from
  // not connected, so this is the whole vocabulary on the "connected" side.
  ios_net_online: 'Connected',

  // Last-check banner (SPEC §11). Android needs none of this: its foreground
  // service keeps running with the screen off, so "when did this phone last
  // report" is never the open question it is here.
  ios_last_check_label: 'Last check',
  ios_last_check_never: 'Nothing reported yet',
  ios_last_check_ago: 'Reported {0}',
  ios_last_check_at: 'at {0}',
  ios_last_check_stale:
    'This device has been silent for over {0} minutes. Check: is the screen off, was the app swiped away, did the phone just restart?',
  ios_next_run: 'Next run in {0}',
  ios_next_run_none: 'No run scheduled',
  ios_running_now: 'A run is in progress…',

  // Relative ages for the banner above. Kept as strings rather than an
  // Intl.RelativeTimeFormat call so the two-part forms ("2 h 15 min ago") read
  // the way each language actually orders them.
  ios_ago_just_now: 'just now',
  ios_ago_seconds: '{0}s ago',
  ios_ago_minutes: '{0} min ago',
  ios_ago_hours: '{0} h {1} min ago',
  ios_ago_days: '{0} d {1} h ago',

  // Batch outcome. Two whole sentences rather than one stem plus a `, {0}
  // skipped` suffix: a fragment that opens with punctuation can only be appended
  // where English happens to put it, and a translator handed it cannot move the
  // clause or change the separator. The saved-only variant keeps its own key
  // because "0 skipped" is noise on every healthy cycle, so the screen picks the
  // sentence it needs instead of gluing one together.
  //
  // `ios_batch_dropped` is not decoration: a link that got fewer rounds than
  // configured is dropped rather than condemned, and a rule that silently
  // discards data is one nobody notices going wrong. It stays on screen.
  ios_batch_saved: 'Last batch: {0} rows saved',
  ios_batch_saved_skipped: 'Last batch: {0} rows saved, {1} skipped',
  ios_batch_dropped: '{0} links did not get every round — deliberately not sent',

  // Cycle phases — only the two that look like a hang from the outside and have
  // nothing else on screen telling the operator what is happening. The idle,
  // done and failed phases are deliberately not narrated: the progress bar, the
  // batch counts and the error module already report them with the numbers
  // attached, and a notice row per phase would narrate the obvious
  // (`ProgressNotice`).
  ios_phase_fetching: 'Fetching config and the link list…',
  ios_phase_submitting: 'Submitting results…',

  // The word the Results list puts on a reachable link — a whole word per
  // locale, never `stat_alive` with a "?" appended in code. The mark is SPEC §13
  // made visible: a reachable probe proves *something* answered, not that the
  // real site did, and an ISP that blocks by DNS-redirecting to a notice page
  // returns a perfectly good response this app cannot tell apart. It is a key of
  // its own for two reasons: appending the mark in code hands CJK a Latin "?"
  // where the language writes "？", and the single most important caveat in the
  // app has to be reviewable per locale rather than hidden inside a template
  // literal. `stat_alive` stays unmarked — as a stat-cell label it names a
  // count, not a claim about one link.
  ios_status_alive_maybe: 'Alive?',

  // A plain http:// link is never called dead. Safari's mixed-content policy is
  // a constant, not a property of the link, and reporting it as a verdict would
  // put a permanent false negative in the fleet data.
  ios_status_unmeasurable: 'not measurable',
  ios_error_mixed_content:
    'plain http:// domain — Safari blocks mixed content. Not a verdict about the link.',

  // Terminal 401 state. Android wipes its key store and returns to key entry;
  // here the key lives in the icon's URL, so there is nothing to wipe and
  // nothing the person holding the phone can type to fix it.
  ios_key_revoked_title: 'Device key rejected',
  ios_key_revoked_body:
    'The backend returned 401 — this key has expired or the device was locked. The app has stopped for good and will not retry.',
  ios_key_revoked_hint:
    "The key lives in the icon's URL, so it cannot be changed from inside the app. Ask an administrator for a new install link, then delete the old icon and Add to Home Screen again.",

  // Settings → Operation. Read-only, replacing Android's auto-run switch and
  // interval chips: this app has no settings of its own and always follows the
  // newest server value, so there is no local value to protect (SPEC §9).
  ios_settings_section_operation: 'Operation',
  ios_settings_schedule: 'Schedule',
  ios_settings_schedule_value: 'Every {0} min — set by the server',
  ios_settings_schedule_default: 'Every {0} min — default, the server config could not be read',
  ios_settings_autorun_off:
    'The server has automatic checks switched off. The device keeps waking and asking, so it can be switched back on remotely.',
  ios_settings_foreground_only:
    'This app only runs while the screen is on and the WebClip is in the foreground. It does not reopen after a restart. That is a platform limit, not a bug.',

  // Settings → Required device setup. This is the honest replacement for
  // Android's battery-optimisation and OEM-autostart buttons: the same problems
  // exist, but nothing in Safari can read or change any of these, so the app
  // asks a human to do it instead of pretending to handle it.
  ios_settings_section_device_setup: 'Required device setup',
  ios_setup_autolock: 'Settings → Display & Brightness → Auto-Lock: Never',
  ios_setup_lowpower: 'Turn Low Power Mode off',
  ios_setup_charger: 'Keep it on a charger',
  ios_setup_guided_access: 'Turn on Guided Access, locked to this WebClip',
  ios_setup_note:
    'None of these can be checked or changed from inside the app — Safari has no access to them.',

  ios_device_platform: 'Platform',
  ios_device_user_agent: 'User agent',

  // The caveat from SPEC §2, on screen rather than only in the docs. The whole
  // reason the `platform` field exists is that these two data sources are not
  // interchangeable, and the person reading the numbers is the one who has to
  // know it.
  ios_caveat:
    '"Alive" here means "probably alive", not "verified alive". This app can only measure whether something is reachable; it cannot read a status code and cannot tell the real site from a carrier\'s block page. Do not pool these numbers with the Android app\'s at one confidence level.',

  ios_copy_failed: 'Could not copy',
};
